import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock, User } from "lucide-react";
import SEO from "@/components/SEO";
import CTABanner from "@/components/CTABanner";
import api from "@/lib/api";

function renderContent(content) {
  return content.split("\n").map((line, i) => {
    if (line.startsWith("## ")) {
      return <h2 key={i} className="mt-8 font-display text-2xl font-extrabold text-dq-ink">{line.slice(3)}</h2>;
    }
    if (!line.trim()) return null;
    return <p key={i} className="mt-4 text-[17px] leading-relaxed text-dq-muted">{line}</p>;
  });
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    setStatus("loading");
    api.get(`/blog/${slug}`)
      .then((r) => { setPost(r.data); setStatus("done"); })
      .catch(() => setStatus("error"));
  }, [slug]);

  if (status === "loading") {
    return <div className="dq-container pt-40 pb-20"><div className="h-96 animate-pulse rounded-3xl bg-white" /></div>;
  }
  if (status === "error" || !post) {
    return (
      <div className="dq-container pt-40 pb-20 text-center">
        <h1 className="font-display text-3xl font-extrabold text-dq-ink">Article not found</h1>
        <Link to="/blog" className="btn-secondary mt-6">Back to blog</Link>
      </div>
    );
  }

  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt,
    author: { "@type": "Organization", name: post.author },
    publisher: { "@type": "Organization", name: "DiziTeQ Innovative Solutions" },
    datePublished: post.created_at,
  };

  return (
    <>
      <SEO title={post.title} description={post.excerpt} path={`/blog/${post.slug}`} type="article"
        image={post.cover_image || "/generated/seo.png"} schema={schema} />

      <article className="pt-36 lg:pt-40">
        <div className="dq-container max-w-3xl">
          <Link to="/blog" data-testid="blog-back" className="link-underline inline-flex items-center gap-1.5 text-sm font-semibold text-dq-muted hover:text-dq-ink">
            <ArrowLeft className="h-4 w-4" /> All articles
          </Link>
          <span className="mt-6 inline-block rounded-full bg-dq-orange/10 px-3 py-1 text-xs font-semibold text-dq-orange">{post.category}</span>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tighter text-dq-ink sm:text-5xl">{post.title}</h1>
          <div className="mt-5 flex items-center gap-5 text-sm text-dq-muted">
            <span className="flex items-center gap-1.5"><User className="h-4 w-4 text-dq-orange" /> {post.author}</span>
            <span className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-dq-orange" /> {post.read_time}</span>
          </div>
        </div>

        <div className="dq-container mt-10 max-w-4xl">
          <div className="overflow-hidden rounded-[2rem] border border-dq-line">
            <img src={post.cover_image || "/generated/seo.png"} alt={post.title} className="aspect-[16/8] w-full object-cover" />
          </div>
        </div>

        <div className="dq-container mt-10 max-w-3xl pb-16" data-testid="blog-content">
          {renderContent(post.content)}
        </div>
      </article>
      <CTABanner />
      <div className="py-10" />
    </>
  );
}
